import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { getPagedCities } from '@/services/city.service';
import { City, CityPaginationFilterRequest, CityStatusList } from '@/models/city.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { getCountriesLookup } from '@/services/country.service';
import FetchingSelect from '@/components/FetchingSelect';
import ImageCell from '@/components/ImageCell';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;
interface CityListProps {
  countryId?: number;
};

const CityList : React.FC<CityListProps> = ({countryId}) => {
  const [filterReq, setFilterReq] = useState<CityPaginationFilterRequest>(new CityPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);
  
  const fetchData = async (req: CityPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        countryId: countryId ?? req.countryId,
      };         
      const result: PagedResponseModel<City> = await getPagedCities(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching cities:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/cities/new`, {state: {countryId}})}>
          Add City
        </Button>

        <Stack spacing={6}>
          {!countryId && <FetchingSelect
            label="Country"
            fetchData={(text, page) => getCountriesLookup(text, page)}
            value={filterReq.countryId}
            onChange={dataKey => setFilterReq(pre => ({...pre, countryId: dataKey}))}
          />}
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />        
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Icon</HeaderCell>
          <ImageCell dataKey="imageUrl" />
        </Column>        
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Code</HeaderCell>
          <Cell dataKey="code" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Country</HeaderCell>
          <Cell dataKey="country.name" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Time Zone Id</HeaderCell>
          <Cell dataKey="ianaTimeZoneId" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Status</HeaderCell>
          <Cell dataKey="status" >
            {(rowData: City) => CityStatusList.findLast(s => s.value == rowData.status)?.label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData : City) => (              
              <IconButton onClick={() => nav(`/cities/${rowData.id}`, {state: {countryId}})} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default CityList;
